import { useTranslation } from 'react-i18next'
import { MessageSquare, Paperclip } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { DrilldownSmsRow } from '@/types/analyticsDrilldown'
import { displayPhone, formatDateTime } from './formatters'

interface Props {
  rows: DrilldownSmsRow[]
  locale: string
  darkMode: boolean
}

export function SmsDrilldownTable({ rows, locale, darkMode }: Props) {
  const { t } = useTranslation()
  const headerBase = cn(
    'px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide',
    darkMode ? 'text-slate-400' : 'text-slate-500',
  )
  const cellBase = cn(
    'px-3 py-3 text-sm',
    darkMode ? 'text-slate-200' : 'text-slate-700',
  )
  const rowBorder = darkMode ? 'border-slate-700/60' : 'border-slate-200/70'

  return (
    <div
      className={cn(
        'rounded-xl border overflow-hidden',
        darkMode ? 'border-slate-700 bg-slate-800/40' : 'border-slate-200 bg-white',
      )}
    >
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead className={cn(darkMode ? 'bg-slate-800/80' : 'bg-slate-50')}>
            <tr className={cn('border-b', rowBorder)}>
              <th className={cn(headerBase, 'pl-4 w-12')}></th>
              <th className={headerBase}>{t('manager.analytics.drilldown.sms.date')}</th>
              <th className={headerBase}>{t('manager.analytics.drilldown.sms.to')}</th>
              <th className={headerBase}>{t('manager.analytics.drilldown.sms.agent')}</th>
              <th className={headerBase}>{t('manager.analytics.drilldown.sms.preview')}</th>
              <th className={cn(headerBase, 'text-right pr-4')}>
                {t('manager.analytics.drilldown.sms.status')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                className={cn(
                  'border-b last:border-b-0 transition-colors',
                  rowBorder,
                  darkMode ? 'hover:bg-slate-800/60' : 'hover:bg-slate-50',
                )}
              >
                <td className={cn(cellBase, 'pl-4')}>
                  <MessageSquare className="h-4 w-4 text-indigo-500" />
                </td>
                <td className={cn(cellBase, 'tabular-nums')}>
                  {formatDateTime(row.created_at, locale)}
                </td>
                <td className={cellBase}>{displayPhone(row.to_number)}</td>
                <td className={cellBase}>
                  {row.sent_by?.name ?? (
                    <span className={cn(darkMode ? 'text-slate-500' : 'text-slate-400')}>–</span>
                  )}
                </td>
                <td className={cn(cellBase, 'max-w-md truncate')}>
                  {row.has_media && (
                    <Paperclip className="inline-block h-3 w-3 mr-1 text-slate-400" />
                  )}
                  <span title={row.message_preview}>{row.message_preview}</span>
                </td>
                <td
                  className={cn(
                    cellBase,
                    'text-right pr-4 text-xs',
                    darkMode ? 'text-slate-500' : 'text-slate-400',
                  )}
                >
                  {row.status}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
